import { ClipboardEvent } from 'react';
import { Descendant, Editor, Node, Element, Range, Transforms } from 'slate';
import { AutocompleteResult, CustomElement, Tag } from 'js/social/shared/types';

// We only allow extended ASCII characters in links.
// eslint-disable-next-line no-control-regex
export const ASCII_REGEX = /[\x00-\xFF]+/g;
export const TAG_REGEX_EXACT = /^[a-zA-Z]* ?[a-zA-Z]+$/;
export const TAG_MAX = 10;
export const HASHTAG_REGEX_BASE = '(?![\\p{N}_-]{1,64}\\b)[\\w-]{1,64}\\b';
export const HASHTAG_REGEX = new RegExp(`\\B#${HASHTAG_REGEX_BASE}`, 'gu');
export const HASHTAG_REGEX_EXACT = new RegExp(`^${HASHTAG_REGEX_BASE}$`, 'u');
export const HASHTAG_MAX = 20;

/**
 * Composes the social editor to know about tags and paper_links.
 * Tags and paper_links should be both inline and void.
 * Inline has a similar meaning
 * to regular CSS (doesn't go to a new line), and void means
 * that the item doesn't have "content". Void allows the cursor
 * to backspace/select the tag in one go.
 * @param editor The editor to compose.
 */
export function withCustomElements(editor: Editor): Editor {
  const { isInline, isVoid } = editor;

  editor.isInline = (element) => {
    return (
      element.type === 'tag' ||
      element.type === 'paper_link' ||
      isInline(element)
    );
  };

  editor.isVoid = (element) => {
    return (
      element.type === 'tag' || element.type === 'paper_link' || isVoid(element)
    );
  };

  return editor;
}

/**
 * Find autocomplete inside a slate editor if it exists.
 * @param editor The Slate editor to check.
 * @param begin The beginning of the autocomplete.
 * @param filter The valid regex for the autocomplete.
 * @param requireContent Whether to require text after `begin` character.
 */
export function findAutocomplete(
  editor: Editor,
  begin: string,
  filter: RegExp,
  requireContent = false,
): Partial<AutocompleteResult> {
  const { selection } = editor;
  if (selection && Range.isCollapsed(selection)) {
    const [cursor] = Range.edges(selection);
    let start = Editor.before(editor, cursor);
    while (start !== undefined) {
      const range = Editor.range(editor, start, cursor);
      const text = Editor.string(editor, range);
      if (text[0] === begin && (!requireContent || text.length > 1)) {
        return { range, search: text.substring(1) };
      } else if (!filter.test(text)) {
        break;
      }
      start = Editor.before(editor, start);
    }
  }
  return { range: undefined, search: undefined };
}

/**
 * Decide if we're actively trying to autocomplete a tag.
 * We allow up to two English words for tagging.
 * Valid: "@", "@hello", "@hello world"
 * Invalid: "@1", "@a b c"
 * @param editor The Slate editor to check.
 */
export function checkTagging(editor: Editor) {
  return findAutocomplete(editor, '@', TAG_REGEX_EXACT);
}

/**
 * Decide if we're actively trying to autocomplete a hashtag.
 * @param editor The Slate editor to check.
 */
export function checkHashtagging(editor: Editor) {
  return findAutocomplete(editor, '#', HASHTAG_REGEX_EXACT, true);
}

function paperLinkRegex() {
  const domainParts = location.hostname.split('.');
  const domain = domainParts
    .slice(Math.max(0, domainParts.length - 2), domainParts.length)
    .join('\\.');
  return new RegExp(
    `\\b((https?://)?(www\\.)?)?${domain}/(\\d+)(/\\S*)?\\b`,
    'g',
  );
}

/**
 * Looks for Academia paper links inside of paragraph elements
 * inside of a Slate editor. Uses the current domain of the page
 * so will differentiate between devdemia.com and academia.edu, etc.
 * @param root The Slate root node to check
 * @returns
 */
export function detectAcademiaPaperLinks(root: Node) {
  const elementNodes = Node.elements(root);
  const links = [];

  for (const [node, path] of elementNodes) {
    if (Element.isElement(node) && node.type === 'paragraph') {
      const textNodes = Node.texts(node);
      for (const [textNode, textPath] of textNodes) {
        const text = textNode.text;
        const matches = text.matchAll(paperLinkRegex());

        for (const match of matches) {
          if (match.index === undefined) {
            continue;
          }

          const start = match.index;
          const end = match.index + match[0].length;
          links.push({
            range: {
              anchor: {
                path: (path as number[]).concat(textPath),
                offset: start,
              },
              focus: {
                path: (path as number[]).concat(textPath),
                offset: end,
              },
            },
            text: match[0],
            workId: parseInt(match[4]),
          });
        }
      }
    }
  }

  return links;
}

/**
 * Determine if we're actively trying to autocomplete any type of content.
 * @param editor The Slate editor to check.
 * @param disableTags Whether tags are allowed.
 * @param disableHashtags Whether hashtags are allowed.
 */
export function checkAutocomplete(
  editor: Editor,
  disableTags: boolean,
  disableHashtags: boolean,
): AutocompleteResult {
  if (!disableTags) {
    const tag = checkTagging(editor);
    if (tag.range) {
      return { type: 'tag', ...tag };
    }
  }

  if (!disableHashtags) {
    const hashtag = checkHashtagging(editor);
    if (hashtag.range) {
      return { type: 'hashtag', ...hashtag };
    }
  }

  return { type: undefined, range: undefined, search: undefined };
}

/**
 * Get a tag's key/identifier.
 * @param tag The tag.
 */
export function getTagKey(tag: Tag) {
  return `${tag.tagType}|${tag.tagId}`;
}

/**
 * Serializes post content/tags into a format compatible
 * with the social editor (Slate).
 * @param content The textual content of the post.
 * @param tags The tags to insert into the post.
 */
export function serializeContent(content: string, tags: Tag[]): Descendant[] {
  const sortedTags = tags.sort((a, b) => a.location - b.location);
  const paragraphs = content.split('\n');

  const result: Descendant[] = [];
  let start = 0;
  for (const paragraph of paragraphs) {
    const end = start + paragraph.length;

    let cursor = 0;
    const children: Descendant[] = [];
    for (const tag of sortedTags.filter(
      (tag) => start <= tag.location && tag.location <= end,
    )) {
      const textLength = tag.location - start;
      children.push({
        text: paragraph.substring(cursor, cursor + textLength),
      });
      children.push({
        type: 'tag',
        data: tag,
        children: [{ text: '' }],
      });
      cursor += textLength;
      start = tag.location;
    }
    children.push({ text: paragraph.substring(cursor) });

    const paperLinkedChildren: Descendant[] = [];
    children.forEach((child) => {
      if ('text' in child) {
        const text = child.text;
        const matches = text.matchAll(paperLinkRegex());
        let textCursor = 0;
        for (const match of matches) {
          if (match.index === undefined) {
            continue;
          }

          const matchStart = match.index;
          const matchEnd = match.index + match[0].length;

          paperLinkedChildren.push({
            text: text.substring(textCursor, matchStart),
          });
          paperLinkedChildren.push({
            type: 'paper_link',
            data: {
              text: match[0],
              workId: parseInt(match[4]),
            },
            children: [{ text: '' }],
          });
          textCursor = matchEnd;
        }

        paperLinkedChildren.push({ text: text.substring(textCursor) });
      } else {
        paperLinkedChildren.push(child);
      }
    });

    const paragraphNode: CustomElement = {
      type: 'paragraph',
      children: paperLinkedChildren,
    };

    result.push(paragraphNode);
    start = end + 1; // Account for newline character
  }

  return result;
}

/**
 * Normalizes post content/tags from the social editor
 * Slate-compatible format back into Academia's format.
 * @param editor The editor to normalize from.
 */
export function normalizeContent(editor: Editor) {
  const tags: Tag[] = [];
  let paragraphs = 0;
  let content = '';
  for (const [node] of Node.nodes(editor)) {
    if ('text' in node) {
      content += node.text;
    } else if (Element.isElementType(node, 'paragraph')) {
      if (paragraphs > 0) {
        content += '\n';
      }
      paragraphs++;
    } else if (Element.isElementType(node, 'tag')) {
      tags.push({
        ...node.data,
        location: content.length,
      });
    } else if (Element.isElementType(node, 'paper_link')) {
      content += node.data.text;
    }
  }
  return { content, tags };
}

/**
 * Intercept the paste event for the social editor.
 * We want to make sure users cannot duplicate any tags,
 * so we strip any duplicates out before performing the paste.
 * @param editor The editor to paste into.
 * @param event The clipboard event.
 */
export function interceptPaste(editor: Editor, event: ClipboardEvent) {
  // Get Slate's clipboard data
  const clipboardData = event.clipboardData;
  if (!clipboardData) return;

  const slateClipboardKey = 'application/x-slate-fragment';
  const data = clipboardData.getData(slateClipboardKey);
  if (data) {
    event.preventDefault();
  } else {
    return;
  }

  // Find tags that aren't in the current selection
  const seenTags = new Set<string>();
  for (const [node, path] of Node.nodes(editor)) {
    if (
      Element.isElementType(node, 'tag') &&
      (!editor.selection || !Range.includes(editor.selection, path))
    ) {
      seenTags.add(getTagKey(node.data));
    }
  }

  // Decode clipboard data and modify to strip duplicate tags
  const paragraphs: CustomElement[] = JSON.parse(
    decodeURIComponent(atob(data)),
  );
  for (const paragraph of paragraphs) {
    paragraph.children = paragraph.children.filter((node) => {
      let seen = false;
      if (Element.isElementType(node, 'tag')) {
        const key = getTagKey(node.data);
        if (seenTags.has(key)) seen = true;
        seenTags.add(key);
      }
      return !seen;
    });
  }

  // Perform the paste!
  if (paragraphs.length > 0) {
    Transforms.insertFragment(editor, paragraphs[0].children);
    Transforms.insertNodes(editor, paragraphs.slice(1));
  }
}

/**
 * A modulus function that accounts for negatives.
 * @param num The number to mod.
 * @param mod The mod value.
 */
export function mod(num: number, mod: number) {
  return ((num % mod) + mod) % mod;
}
